import { useCallback, Suspense } from 'react';
import { useLocation, useLoaderData, useNavigate, useSubmit, useRevalidator, Link as RRLink } from 'react-router';
import { useIntl } from 'react-intl';
import moment from 'moment';
import Page from 'client/components/aria/Page/Page';
import LoadingScreen from 'client/components/suc/LoadingScreen/LoadingScreen';
import Button from 'client/components/aria/Button/Button';
import Alert from 'client/components/aria/Alert/Alert';
import defaultAriaProps from 'client/utils/constants/ariaPropTypes';
import WorkpackAlertSuccess from 'client/pages/WorkpacksPage/components/WorkpackAlerts/WorkpackAlertSuccess/WorkpackAlertSuccess';
import WorkpackDetails from 'client/pages/WorkpacksPage/components/WorkpackDetails/WorkpackDetails';
import Link from 'client/components/aria/Link/Link';
import SummaryItem from 'client/components/ui/SummaryItem/SummaryItem';
import { MAP_PAGE, WORKPACKS_PAGE, CREATE_ENQUIRY_PAGE } from 'client/utils/routes/routeConstants';
import buildEnquiryDraftFormData from 'client/utils/helpers/Enquiry/enquiryDraftUtils';
import WorkpackPageTitle from '../components/WorkpackPageTitle/WorkpackPageTitle';
import WarningText from 'client/components/aria/WarningText/WarningText';
import WorkpackStatus from '../components/WorkpackStatus/WorkpackStatus';
import { getWorkpackStatus } from '../ListWorkpackPage/utils/workpackUtils';
import WorkpackDownloadButton from '../components/WorkpackDownloadButton/WorkpackDownloadButton';
// TODO: Re-enable omission components once refactored printspec service is available
// import { getOmittedReasonFromOwners } from 'client/utils/helpers/PrintSpec/assetOwnersOmittedFromPDF';
import { electricitySymbologyFeatureFlag, electricitySymbologyFeatureFlagDate, enquiryFeatureFlag } from 'client/utils/constants/frontEndConfig';
import styles from './ViewWorkpackPage.module.scss';
import WorkpackAlertWarning from '../components/WorkpackAlerts/WorkpackAlertWarning/WorkpackAlertWarning';
import { useWorkpackDownload } from '../hooks/useWorkpackDownload';
import { usePollWorkpacks } from '../ListWorkpackPage/usePollWorkpacks';
import OptIn from 'client/components/aria/OptIn/OptIn';
import Icon from '@mdi/react';
import { mdiOpenInNew } from '@mdi/js';
// import WorkpackOmissionModal from '../components/Omissions/WorkpackOmissionModal/WorkpackOmissionModal';
// import WorkpackAlertOmission from '../components/WorkpackAlerts/WorkpackAlertOmission/WorkpackAlertOmission';

const FEEDBACK_LINK = 'https://forms.cloud.microsoft/e/24BY96fiVe';

const WORKPACK_STATUS = {
    AVAILABLE: 'AVAILABLE'
};

const getAvailableWorkpacks = (workpacks) => {
    return (workpacks || []).filter(pack => pack.status === WORKPACK_STATUS.AVAILABLE);
};

const getLatestAvailableWorkpack = (workpacks) => {
    return getAvailableWorkpacks(workpacks).sort((packA, packB) => {
        return new Date(packB.dateOfPrintSpec) - new Date(packA.dateOfPrintSpec);
    })[0];
};

const ViewWorkpackPage = () => {
    const { workpackRequest } = useLoaderData();
    const intl = useIntl();
    const { state } = useLocation();
    const navigate = useNavigate();
    const submit = useSubmit();
    const { downloadErrorMessage, downloadWorkpack } = useWorkpackDownload('viewWorkpackPage');

    const { revalidate } = useRevalidator();
    // make single workpack match the same structure as list page for polling
    const workpackRequests = { content: [workpackRequest] };
    usePollWorkpacks({ workpackRequests, revalidate });
    // TODO: Re-enable once refactored printspec service provides omitted owners data
    // const omittedOwners = getOmittedReasonFromOwners(assetOwners?.assetsInAOIOwners || []);

    const openWorkpack = useCallback((workpackRequest) => {
        const pack = getLatestAvailableWorkpack(workpackRequest.workpacks);
        return downloadWorkpack(workpackRequest.workpackReqId, pack, workpackRequest.title);
    }, [downloadWorkpack]);

    // TODO: Re-enable once refactored printspec service provides omitted owners data (used by WorkpackOmissionModal)
    // const [showWarningDialog, setShowWarningDialog] = useState(false);
    // useEffect(() => {
    //     setShowWarningDialog(state?.justCreated);
    // }, []);

    const renderWorkpackPageContent = () => {
        const hasAvailableWorkpacks = getAvailableWorkpacks(workpackRequest.workpacks).length > 0;
        const predefinedPurposes = ['prework', 'planned', 'emergency'];
        const isOtherPurpose = Boolean(workpackRequest?.purpose) && !predefinedPurposes.includes(workpackRequest.purpose);

        const getPurposeDisplay = () => {
            if (!workpackRequest?.purpose) {
                return 'N/A';
            }

            if (workpackRequest.purpose === 'prework') {
                return intl.formatMessage({ id: 'createWorkpackPage.purpose.prework' });
            }

            if (workpackRequest.purpose === 'planned') {
                return intl.formatMessage({ id: 'createWorkpackPage.purpose.planned' });
            }

            if (workpackRequest.purpose === 'emergency') {
                return intl.formatMessage({ id: 'createWorkpackPage.purpose.emergency' });
            }

            if (isOtherPurpose) {
                return intl.formatMessage({ id: 'createWorkpackPage.purpose.other' });
            }

            return workpackRequest.purpose;
        };

        const purposeDescription = isOtherPurpose
            ? workpackRequest.purpose
            : null;

        const showPurposeDescription = Boolean(purposeDescription?.trim());

        const pdfStatus = getWorkpackStatus(workpackRequest.workpacks, workpackRequest.dateCreated);
        let pdfValidityMessage = null;
        let warningContainerClass = 'govuk-grid-column-three-quarters';

        if (pdfStatus) {
            const isPdfExpired = pdfStatus.isExpired;
            warningContainerClass = `govuk-grid-column-three-quarters${isPdfExpired ? ` ${styles.warningBoxContainer}` : ''}`;

            pdfValidityMessage = {
                message: intl.formatMessage({
                    id: isPdfExpired
                        ? 'viewWorkpackPage.pdfValidityWarning.expired'
                        : 'viewWorkpackPage.pdfValidityWarning.valid'
                }),
                variant: isPdfExpired ? 'triangle' : 'circle'
            };
        }

        const workpackStatus = pdfStatus;

        const startMakeEnquiryFlow = () => {
            if (!workpackRequest?.geometry) {
                navigate(CREATE_ENQUIRY_PAGE);
                return;
            }

            const inferredEmergencyFlag = typeof workpackRequest?.isEmergency === 'boolean'
                ? workpackRequest.isEmergency
                : workpackRequest?.purpose === 'emergency';

            const utilityEnquiryType = workpackRequest?.purpose === 'emergency'
                ? 'unplanned'
                : (workpackRequest?.purpose === 'planned' ? 'planned' : null);

            const startDate = inferredEmergencyFlag
                ? workpackRequest?.startDate ?? (() => {
                    const emergencyStart = new Date();
                    emergencyStart.setHours(0, 0, 0, 0);
                    return emergencyStart.toISOString();
                })()
                : workpackRequest?.startDate;

            const formData = buildEnquiryDraftFormData({
                geometry: workpackRequest.geometry,
                title: workpackRequest.title,
                description: workpackRequest.description,
                jobReference: workpackRequest.jobReference,
                activityType: workpackRequest.activityType,
                isEmergency: inferredEmergencyFlag,
                utilityEnquiryType,
                startDate,
                endDate: inferredEmergencyFlag ? null : workpackRequest?.endDate
            });

            submit(formData, {
                action: CREATE_ENQUIRY_PAGE,
                method: 'put'
            });
        };

        return (
            <>
                {downloadErrorMessage && (
                    <div className="govuk-grid-row govuk-!-margin-top-4">
                        <div className="govuk-grid-column-full">
                            <Alert
                                variant="error"
                                title={intl.formatMessage({ id: 'viewWorkpackPage.download.error.title' })}
                                data-testid="workpack-download-error-alert"
                            >
                                <p className="govuk-body">{downloadErrorMessage}</p>
                            </Alert>
                        </div>
                    </div>
                )}
                {state?.justCreated && (
                    <>
                        <div className="govuk-grid-row govuk-!-margin-top-4">
                            <div className="govuk-grid-column-full">
                                <WorkpackAlertSuccess name={workpackRequest.title} />
                            </div>
                        </div>
                        {/* TODO: Re-enable once refactored printspec service provides omitted owners data
                        {omittedOwners.length > 0 && (
                            <div className="govuk-grid-row">
                                <div className="govuk-grid-column-full">
                                    <WorkpackAlertOmission omittedOwners={omittedOwners} />
                                </div>
                            </div>
                        )}
                        */}
                        <div className="govuk-grid-row govuk-!-margin-top-4">
                            <div className="govuk-grid-column-two-thirds">
                                <WorkpackDetails>
                                    <p className="govuk-body" data-testid="workpack-confirmation-reference-message">
                                        <strong data-testid="workpack-confirmation-reference-name">{workpackRequest.title}</strong> {intl.formatMessage({ id: 'viewWorkpackPage.sub.title' })} <strong data-testid="workpack-confirmation-reference-id">{workpackRequest.workpackReqId}</strong>
                                    </p>
                                </WorkpackDetails>
                            </div>
                        </div>
                        {hasAvailableWorkpacks && (
                            <div className="govuk-grid-row govuk-!-margin-top-4">
                                <div className="govuk-grid-column-full">
                                    <WorkpackDownloadButton
                                        onPress={() => openWorkpack(workpackRequest)}
                                        data-testid="workpack-confirmation-download-button"
                                    >
                                        {intl.formatMessage({ id: 'viewWorkpackPage.workpack' })}
                                    </WorkpackDownloadButton>
                                </div>
                            </div>
                        )}
                        <div className="govuk-grid-row govuk-!-margin-top-4">
                            <div className="govuk-grid-column-full">
                                <WorkpackDetails>
                                    <p className="govuk-body" data-testid="workpack-confirmation-links-message">
                                        {intl.formatMessage({ id: 'viewWorkpackPage.pdfLink.text' }, {
                                            pdfLink: str => (
                                                <Link href={WORKPACKS_PAGE} data-testid="workpack-confirmation-workpacks-link">{str}</Link>
                                            )
                                        })}{intl.formatMessage({ id: 'viewWorkpackPage.workpack.backToMap' }, {
                                            backToMapLink: str => (
                                                <Link href={MAP_PAGE} data-testid="workpack-confirmation-map-link">{str}</Link>
                                            )
                                        })}
                                    </p>
                                </WorkpackDetails>
                            </div>
                        </div>
                        {enquiryFeatureFlag && (
                            <>
                                <div className="govuk-grid-row govuk-!-margin-top-6">
                                    <div className="govuk-grid-column-full">
                                        <hr className="govuk-section-break govuk-section-break--l govuk-section-break--visible" />
                                    </div>
                                </div>
                                <div className="govuk-grid-row govuk-!-margin-top-4">
                                    <div className="govuk-grid-column-full">
                                        <WorkpackDetails data-testid="workpack-confirmation-next-steps-title">{intl.formatMessage({ id: 'viewWorkpackPage.nextSteps.title' })}</WorkpackDetails>
                                    </div>
                                </div>
                                <div className="govuk-grid-row">
                                    <div className="govuk-grid-column-two-thirds">
                                        <p className="govuk-body" data-testid="workpack-confirmation-next-steps-description">
                                            {intl.formatMessage({ id: 'viewWorkpackPage.nextSteps.description' })}
                                        </p>
                                    </div>
                                </div>
                                <div className="govuk-grid-row govuk-!-margin-bottom-4">
                                    <div className="govuk-grid-column-full">
                                        <Button
                                            onPress={startMakeEnquiryFlow}
                                            variant="info"
                                            data-testid='makeEnquiryButton'
                                        >
                                            {intl.formatMessage({ id: 'viewWorkpackPage.nextSteps.makeEnquiry' })}
                                        </Button>
                                    </div>
                                </div>
                            </>
                        )}
                        {/* TODO: Re-enable once refactored printspec service provides omitted owners data
                        <WorkpackOmissionModal showWarningDialog={showWarningDialog} setShowWarningDialog={setShowWarningDialog} omittedOwners={omittedOwners} />
                        */}
                    </>
                )}
                {!state?.justCreated && (
                    <>
                        <div className="govuk-grid-row govuk-!-margin-top-4">
                            <div className="govuk-grid-column-full">
                                <WorkpackPageTitle data-testid="workpack-view-page-title">
                                    {workpackRequest.title}
                                </WorkpackPageTitle>
                            </div>
                        </div>
                        {/* {omittedOwners.length > 0 && (
                            <div className="govuk-grid-row">
                                <div className="govuk-grid-column-full">
                                    <WorkpackAlertOmission omittedOwners={omittedOwners} />
                                </div>
                            </div>
                        )} */}

                        <WorkpackAlertWarning />
                        <div className="govuk-grid-row govuk-!-margin-0">
                            <div className={warningContainerClass}>
                                {pdfValidityMessage && <WarningText {...pdfValidityMessage} data-testid="pdf-validity-message" />}
                            </div>
                        </div>
                        <div className="govuk-grid-row govuk-!-margin-top-4">
                            <div className="govuk-grid-column-full">
                                <WorkpackDetails>
                                    {intl.formatMessage({ id: 'viewWorkpackPage.summary.details.title' })}
                                </WorkpackDetails>
                            </div>
                        </div>
                        <div className="govuk-grid-row govuk-!-margin-top-4">
                            <div className="govuk-grid-column-full">
                                <SummaryItem
                                    title={intl.formatMessage({ id: 'viewWorkpackPage.summary.dateCreated' })}
                                    data-testid="workpack-view-dateCreated"
                                >
                                    {moment(workpackRequest.dateCreated).format('D MMM YYYY, h:mma')}
                                </SummaryItem>
                                <hr className="govuk-section-break govuk-section-break--m govuk-section-break--visible" />
                                <SummaryItem
                                    title={intl.formatMessage({ id: 'viewWorkpackPage.summary.status' })}
                                    data-testid="workpack-view-status"
                                >
                                    <WorkpackStatus
                                        displayStatus={workpackStatus.displayStatus}
                                        daysRemaining={workpackStatus.daysRemaining}
                                    />
                                </SummaryItem>
                                {hasAvailableWorkpacks && <>
                                    <hr className="govuk-section-break govuk-section-break--m govuk-section-break--visible" />
                                    <SummaryItem
                                        title={intl.formatMessage({ id: 'viewWorkpackPage.summary.download' })}
                                        data-testid="workpack-view-download"
                                    >
                                        <WorkpackDownloadButton
                                            onPress={() => openWorkpack(workpackRequest)}
                                            data-testid="workpack-view-download-button"
                                        >
                                            {intl.formatMessage({ id: 'viewWorkpackPage.workpack' })}
                                        </WorkpackDownloadButton>
                                    </SummaryItem>
                                </>
                                }
                                {enquiryFeatureFlag && (
                                    <>
                                        <hr className="govuk-section-break govuk-section-break--m govuk-section-break--visible" />
                                        <div className="govuk-!-margin-top-8">
                                            <WorkpackDetails>
                                                {intl.formatMessage({ id: 'viewWorkpackPage.summary.workpackType.title' })}
                                            </WorkpackDetails>
                                        </div>

                                        <SummaryItem
                                            title={intl.formatMessage({ id: 'viewWorkpackPage.summary.purpose' })}
                                            data-testid="workpack-view-purpose"
                                        >
                                            {getPurposeDisplay()}
                                        </SummaryItem>
                                    </>
                                )}
                                {showPurposeDescription && (
                                    <>
                                        <hr className="govuk-section-break govuk-section-break--m govuk-section-break--visible" />
                                        <SummaryItem
                                            title={intl.formatMessage({ id: 'viewWorkpackPage.summary.description' })}
                                            data-testid="workpack-view-description"
                                        >
                                            {purposeDescription}
                                        </SummaryItem>
                                    </>
                                )}

                                {electricitySymbologyFeatureFlag && new Date(workpackRequest.dateCreated) >= new Date(electricitySymbologyFeatureFlagDate) && (
                                    <OptIn as='section'>
                                        <OptIn.Header className='govuk-!-padding-top-2'>{intl.formatMessage({ id: 'viewWorkpackPage.summary.electricitySymbology.title' })}</OptIn.Header>
                                        <OptIn.Description className="govuk-!-margin-top-4">{intl.formatMessage({ id: 'viewWorkpackPage.summary.electricitySymbology.description' })}</OptIn.Description>
                                        <OptIn.Description>
                                            {intl.formatMessage({ id: 'viewWorkpackPage.summary.electricitySymbology.feedback' }, {
                                                externalLink: (str) => (
                                                    <RRLink
                                                        className={['govuk-link', styles.externalLink].join(' ')}
                                                        to={FEEDBACK_LINK}
                                                        target="_blank"
                                                        data-testid="workpack-view-electricity-symbology-feedback-link"
                                                    >
                                                        {str}
                                                        <Icon path={mdiOpenInNew} size={1} />
                                                    </RRLink>
                                                )
                                            })}
                                        </OptIn.Description>
                                    </OptIn>
                                )}
                            </div>
                        </div>
                    </>
                )}
            </>
        );
    };

    return (
        <Page title={intl.formatMessage({ id: 'viewWorkpackPage.page.title' })} data-testid="viewWorkpackPage">
            <Suspense fallback={<LoadingScreen />}>
                {renderWorkpackPageContent()}
            </Suspense></Page>
    );
};

ViewWorkpackPage.propTypes = {
    ...defaultAriaProps
};

export default ViewWorkpackPage;
